// ─────────────────────────────────────────────────────────────────
// Phase J + L — Editeur de plan complet pour Etape 6 du parcours
// Date : 2026-05-10
//
// UI propre alignee sur le design system journey.css. Remplace l'embed
// de NutritionConsultation dans l'etape 6.
//
// Fonctionnalites V2 (Phase L) :
//   - Lecture du dernier plan (local store + Supabase fallback)
//   - Genération IA avec note libre + directives persistantes
//   - Modal d'apercu plein ecran lisible (markdown render simple)
//   - Edition markdown libre (textarea grande)
//   - Sauvegarde Supabase
//   - Export Word officiel réservé à l'étape Livraison
//   - Onglet Fiche frigo (build + apercu + export Word)
//   - Reformulation IA d'une selection / paragraphe
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { saveNutritionConsultationConfirmed } from './store';
import { callClaude } from './services/anthropic';
import { trackPlanGenerated, trackPlanGenerationFailed, trackPlanModification } from './services/observability';
// V97.58 : buildSystemPromptFrV2 déplacé dans composer.fr (cassage du cycle fr ↔ composer)
import { buildSystemPromptFrV2 } from './services/prompts/nutrition/composer.fr';
// V97.60 : bloc sécurité injecté dans les réécritures de section ("Améliorer")
import { buildSafetyBlockFr } from './services/prompts/nutrition/_clinicalContext.fr';
import CockpitErrorBoundary from './components/CockpitErrorBoundary';
// V97.27 — Services anti-slop / guardrails entierement deplaces dans
// les hooks (useSlopAudit, useGuardrailsAudit) et les composants extraits
// (AntiSlopSection, GuardrailsAuditBanner). JourneyPlanEditor n'importe
// plus rien de ces services directement.
// V97.20 (OBS-1) — Tracking des generations de plans.
// V97.27 — recordSlopAction + updateSlopFlagsCount deplaces dans useSlopAudit.
import { recordPlanGeneration } from './services/planObservability';
// V97.27 (refacto) — Hooks et components extraits.
import { useSlopAudit } from './hooks/useSlopAudit';
import { useGuardrailsAudit } from './hooks/useGuardrailsAudit';
import { usePlanAutosave } from './hooks/usePlanAutosave';
import { useModalA11y } from './hooks/useModalA11y';
import { GuardrailsAuditBanner } from './components/GuardrailsAuditBanner';
import { AntiSlopSection } from './components/AntiSlopSection';
// V97.22.3 (V97.18 Phase D) — Phase active du parcours injectee dans le composer.
import { resolveProtocolPhaseForGeneration } from './services/resolveProtocolPhaseForGeneration';
import { lireDatesActivationPhasesVite } from './suivi/services/useDateActivationSuivi';
// V97.4 Phase V2.B : constructeur clinicalContext depuis journey_state.
import { buildClinicalContext, filterNonMgdResultsData } from './services/clinical/buildClinicalContext';
import { mgdReviewV2Client } from './services/mgdReviewV2Client';
import { formatBlocages } from './services/clinical/resolveClinicalState';
import { COACH_IDENTITY } from './services/coachIdentity';
import { assertPlanClinicallyCleared, formatClearanceForConfirm } from './services/clinicalClearance';
import { traceClinicalOverride, CLINICAL_OVERRIDE_DOORS } from './services/clinicalOverrideAudit';
import { structurePlanSections } from './services/planFormatters';
import { analyzeFullPlan } from './services/aiClient';
import FicheFrigoPreview from './FicheFrigoPreview';
// 2026-07-26 — Structure des repas au cadrage (collations pilotées par Anissa).
import { MEAL_STRUCTURES, buildMealStructureConstraintFr } from './services/prompts/nutrition/mealStructure.fr';
// « Document résultats » — livrable Word dédié (mot d'explication des analyses).
import ResultsExplanationTab from './ResultsExplanationTab';
import { getNutritionPlanMode } from './services/nutritionPlanMode';
import { getClientNutritionLocale } from './services/nutritionLocale';
import { hasRiskProfileAssessment } from './services/riskProfileFlags';
import { preloadClinicalPolicyRegistryFromSupabase,
  assertMgdClinicalPolicyReady } from './services/clinicalPolicyRegistry';
import { stripAdministrativePromptFields } from './services/promptFormPrivacy';
import {
  awaitConfirmedNutritionReload,
  normalizeNutritionConsultationCloudRow,
} from './services/nutritionConsultationPersistence';
import {
  isExternalPlanInput,
  requiresMgdPlanAdoption,
} from './services/externalPlanWorkflow';
import { readExternalDossierSynthesisForGeneration } from './services/externalDossierSynthesisAuthority';
import { externalPlanAuthorityClient } from './services/externalPlanAuthority';
import ClientWeekWorkshop from './components/ClientWeekWorkshop';
import RecipesTab from './RecipesTab';
import { buildClientAppPlanFromConsultation } from './services/clientAppMapper';
import {
  buildRecipeAuthoringMeals,
  buildSingleRecipeRequest,
  buildSlotEditorDraft,
  reconcileMealRecipesForSlot,
  upsertWeekMealOverride,
} from './services/weekWorkshop';

export const CLIENT_FIRST_NAME_TOKEN = '{{PRENOM_CLIENTE}}';

export function resolveJourneyPlanEditorTabs(updateScope) {
  if (updateScope === 'menus_recipes') return Object.freeze(['week', 'recipes']);
  if (updateScope === null || updateScope === undefined
      || updateScope === 'targeted' || updateScope === 'full') {
    return Object.freeze(['plan', 'week', 'recipes', 'fridge']);
  }
  return Object.freeze([]);
}

export function canRegenerateJourneyPlan(updateScope) {
  return updateScope === null || updateScope === undefined || updateScope === 'full';
}

export function resolveExternalDossierSynthesis(journeyState) {
  const source = journeyState?.external_dossier_synthesis;
  if (!source || source.review_status !== 'reviewed'
      || source.privacy_review_confirmed !== true
      || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        .test(source.synthesis_id || '')
      || !/^[a-f0-9]{64}$/.test(source.synthesis_sha256 || '')
      || !/^[a-f0-9]{64}$/.test(source.profile_summary_sha256 || '')) return null;
  const { text: _discardedText, ...receipt } = source;
  return receipt;
}

export function buildExternalSynthesisDirective(synthesisText) {
  const exact = String(synthesisText || '').replace(/\r\n/g, '\n').trim();
  if (!exact) return '';
  return [
    'SOURCE EXTERNE RELUE — SYNTHÈSE DU DOSSIER PILOTE',
    'Conserver les valeurs exactes comme faits de source. Ne pas les convertir, corriger ou compléter. N’utiliser pour le plan que les axes explicitement présents dans la synthèse et compatibles avec l’anamnèse et les contraintes. Les points « À valider » restent non résolus.',
    '<<< DÉBUT SYNTHÈSE EXTERNE >>>', exact, '<<< FIN SYNTHÈSE EXTERNE >>>',
  ].join('\n');
}

// eslint-disable-next-line react-refresh/only-export-components
export function resolveGenerationLaunchGate({
  externalSynthesis,
  mealStructure,
  mgdStatus,
  mgdStatusState,
}) {
  const usesExternalSynthesis = Boolean(externalSynthesis);
  const serverStatusReady = mgdStatusState === 'ready';
  const activeMgdRequiresReview = !usesExternalSynthesis
    && (mgdStatus?.reports?.length || 0) > 0
    && mgdStatus?.ready !== true;
  return {
    usesExternalSynthesis,
    serverStatusReady,
    activeMgdRequiresReview,
    disabled: !mealStructure || !serverStatusReady || activeMgdRequiresReview,
  };
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function calculateAgeFromBirthDate(birthDate, now = new Date()) {
  if (!birthDate) return null;
  const birth = new Date(`${birthDate}T00:00:00`);
  const reference = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(reference.getTime()) || birth > reference) return null;
  let age = reference.getFullYear() - birth.getFullYear();
  const monthDelta = reference.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && reference.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age <= 120 ? age : null;
}

function normalizeRecordedAge(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const age = Number(value);
  return Number.isInteger(age) && age >= 0 && age <= 120 ? age : null;
}

export function minimizeAnthropicText(value, {
  firstName, lastName, email, phone, birthDate, age,
} = {}) {
  let safe = String(value ?? '');
  const identities = [firstName, lastName, email, phone]
    .filter((item) => typeof item === 'string' && item.trim())
    .map((item) => item.trim());
  for (const identityValue of identities) {
    const standaloneName = new RegExp(
      `(?<![\\p{L}\\p{N}])${escapeRegExp(identityValue)}(?![\\p{L}\\p{N}])`,
      'giu',
    );
    safe = safe.replace(standaloneName, CLIENT_FIRST_NAME_TOKEN);
  }
  if (birthDate) {
    const replacement = Number.isInteger(age) ? `${age} ans` : 'date de naissance masquée';
    safe = safe.replace(new RegExp(escapeRegExp(birthDate), 'g'), replacement);
  }
  return safe;
}

function minimizeAnthropicValue(value, identity) {
  if (typeof value === 'string') return minimizeAnthropicText(value, identity);
  if (Array.isArray(value)) return value.map((item) => minimizeAnthropicValue(item, identity));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, minimizeAnthropicValue(item, identity)]),
  );
}

export function buildAnthropicSafeForm(form = {}, now = new Date()) {
  const age = calculateAgeFromBirthDate(form.dateNaissance || form.date_naissance, now)
    ?? normalizeRecordedAge(form.age);
  const identity = {
    firstName: form.prenom,
    lastName: form.nom,
    email: form.email,
    phone: form.telephone || form.phone,
    birthDate: form.dateNaissance || form.date_naissance,
    age,
  };
  const promptForm = stripAdministrativePromptFields(form);
  return {
    ...minimizeAnthropicValue(promptForm, identity),
    prenom: CLIENT_FIRST_NAME_TOKEN,
    nom: '',
    email: '',
    telephone: '',
    phone: '',
    dateNaissance: '',
    date_naissance: '',
    ...(Number.isInteger(age) ? { age } : {}),
  };
}

export function rehydrateClientFirstName(value, firstName) {
  const replacement = typeof firstName === 'string' && firstName.trim() ? firstName.trim() : 'Cliente';
  return String(value ?? '').split(CLIENT_FIRST_NAME_TOKEN).join(replacement);
}

function mgdAdoptionErrorMessage(error) {
  const messages = {
    mgd_revision_stale: 'La revue MGD a changé. Retourne à « Revoir les résultats » avant d’adopter.',
    clinical_clearance_required: 'La clairance clinique du plan doit être revue. À valider Anissa.',
    safety_context_missing: 'Les confirmations de sécurité de la fiche sont incomplètes. À valider Anissa.',
    clinical_policy_stale: 'Le registre clinique a changé. Actualise la revue. À valider Anissa.',
    clinical_policy_unsealed: 'Le registre clinique doit être scellé par Anissa.',
  };
  return messages[error?.code] || 'Autorisation serveur indisponible. À valider Anissa.';
}

function rehydrateClientIdentityValue(value, firstName) {
  if (typeof value === 'string') return rehydrateClientFirstName(value, firstName);
  if (Array.isArray(value)) return value.map((item) => rehydrateClientIdentityValue(item, firstName));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key, rehydrateClientIdentityValue(item, firstName)]),
  );
}

export function buildResultsClientForAi(client) {
  const journey = client?.journey_state || {};
  const filteredResults = filterNonMgdResultsData(journey.results_data);
  if (filteredResults.from_plan.length === 0 && filteredResults.external.length === 0) return null;
  return {
    ...client,
    journey_state: {
      ...journey,
      results_data: filteredResults,
    },
  };
}

export function buildResultsExplanationClient(client) {
  if (!client?.id) return null;
  return {
    id: client.id,
    prenom: client.prenom || '',
    nom: client.nom || '',
    form: client.form && typeof client.form === 'object' ? client.form : {},
    journey_state: { results_data: {} },
  };
}

async function sha256Text(value) {
  if (!globalThis.crypto?.subtle) throw new Error('Vérification cryptographique indisponible.');
  const bytes = new TextEncoder().encode(String(value ?? '').replace(/\r\n/g, '\n'));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function assertBrowserAiActionAuthority({
  clientId,
  consultation,
  planText,
  mgdClient = mgdReviewV2Client,
  externalAuthorityClient = externalPlanAuthorityClient,
}) {
  if (!clientId) throw new Error('Cliente introuvable. Action assistée bloquée.');
  let status;
  try {
    status = await mgdClient.status(clientId);
  } catch (caught) {
    throw new Error(caught?.message || 'Validation serveur indisponible. Action bloquée.');
  }
  if (!status || !Array.isArray(status.reports)) {
    throw new Error('Statut serveur invalide. Action bloquée.');
  }

  const externalInput = consultation?.editorial_overrides?.external_plan_input;
  const externalModeDeclared = externalInput?.plan_input_mode
    === 'external_anonymized_dossier_v1';
  if (externalModeDeclared) {
    if (!isExternalPlanInput(externalInput) || !consultation?.id) {
      throw new Error('Preuve externe invalide. Action assistée bloquée.');
    }
    const currentPlanHash = await sha256Text(planText);
    let authority;
    try {
      authority = await externalAuthorityClient.status({
        clientId,
        consultationId: consultation.id,
        planHash: currentPlanHash,
        profileSummaryHash: externalInput.profile_summary_sha256,
      });
    } catch (caught) {
      throw new Error(caught?.message || 'Autorité du plan externe indisponible. Action assistée bloquée.');
    }
    if (authority?.authorized !== true || authority?.persisted !== true
        || authority.planHash !== currentPlanHash) {
      throw new Error('Le plan externe courant n’est pas autorisé et persisté. Action assistée bloquée.');
    }
    return Object.freeze({ mode: 'external', status, authority, currentPlanHash });
  }

  if (status.reports.length > 0) {
    if (consultation?.id) {
      await mgdClient.planProofStatus({ clientId, consultationId: consultation.id });
    }
      throw new Error('Cette action est indisponible pour cette ancienne version du dossier.');
  }
  return Object.freeze({ mode: 'standard', status, authority: null, currentPlanHash: null });
}

export default function JourneyPlanEditor({
  client,
  onPlanSaved,
  onSaveStateChange,
  onOpenClientPreview,
  controlledAiDirectives,
  existingConsultationTarget = null,
  onBeforePersist,
  initialTab = 'plan',
  updateScope = null,
}) {
  const allowedTabs = resolveJourneyPlanEditorTabs(updateScope);
  const menusRecipesOnly = updateScope === 'menus_recipes';
  const canRegenerateFullPlan = canRegenerateJourneyPlan(updateScope);
  const [tab, setTab] = useState(() => (
    allowedTabs.includes(initialTab)
      ? initialTab
      : (allowedTabs.includes('recipes') ? 'recipes' : (allowedTabs[0] || 'blocked'))
  ));
  const [planText, setPlanText] = useState('');
  const [aiDirectives, setAiDirectives] = useState('');
  const resultsExplanationClient = useMemo(() => buildResultsExplanationClient(client), [client]);

  // V97.13.x : sync depuis la sidebar parent (ClientJourneyPage) quand prop fournie.
  // Évite la race condition où l'éditeur a une copie stale du textarea sidebar.
  useEffect(() => {
    if (controlledAiDirectives !== undefined && controlledAiDirectives !== aiDirectives) {
      setAiDirectives(controlledAiDirectives);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledAiDirectives]);
  const [consultation, setConsultation] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [showGenModal, setShowGenModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFridgeModal, setShowFridgeModal] = useState(false);
  const [error, setError] = useState(null);
  const existingConsultationTargetId = String(existingConsultationTarget?.id || '');
  // Phase AP : audit IA du plan
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [mgdCaseState, setMgdCaseState] = useState('checking');
  const [weekWorkshopSaving, setWeekWorkshopSaving] = useState(false);
  const [weekWorkshopError, setWeekWorkshopError] = useState(null);
  const [weekWorkshopSavedAt, setWeekWorkshopSavedAt] = useState(null);
  const [generatingRecipeKey, setGeneratingRecipeKey] = useState(null);
  const consultationPersistenceRef = useRef(null);
  const mgdGenerationProofIdRef = useRef(null);
  // Phase AM : auto-save debouncé (état : idle | dirty | saving | saved | error)
  // V97.27 (refacto) — Autosave debounce extrait dans usePlanAutosave.
  // Hook initialise plus bas car onSave depend de handleSave (lui-meme
  // depend des states qui sont declares apres).
  // savedToast supprimé Phase AM (remplacé par AutosaveIndicator persistant)

  // Phase Q : split en sections + IA par section
  const sections = useMemo(() => splitPlanIntoSections(planText), [planText]);
  const [aiBusy, setAiBusy] = useState(null); // { sectionIndex, action } | null
  const [aiError, setAiError] = useState(null);

  const updateSection = (index, newContent) => {
    const next = sections.map((s, i) => i === index ? { ...s, content: newContent } : s);
    setPlanText(joinSectionsIntoPlan(next));
  };
  const updateSectionTitle = (index, newTitle) => {
    const next = sections.map((s, i) => i === index ? { ...s, title: newTitle } : s);
    setPlanText(joinSectionsIntoPlan(next));
  };
  const removeSection = (index) => {
    if (!window.confirm(`Supprimer la section "${sections[index].title}" ?`)) return;
    const next = sections.filter((_, i) => i !== index);
    setPlanText(joinSectionsIntoPlan(next));
  };
  const insertSectionAfter = (index) => {
    const next = [...sections];
    next.splice(index + 1, 0, { title: 'NOUVELLE SECTION', content: '' });
    setPlanText(joinSectionsIntoPlan(next));
  };

  const loadLatest = useCallback(async () => {
    if (!client?.id) {
      setLoadingInitial(false);
      return;
    }
    setLoadingInitial(true);
    if (existingConsultationTargetId) {
      const targetClientId = existingConsultationTarget?.clientId
        || existingConsultationTarget?.client_id;
      if (targetClientId !== client.id) {
        setError('Le brouillon demandé ne correspond pas à cette cliente.');
        setConsultation(null);
        setPlanText('');
        setLoadingInitial(false);
        return;
      }
      const { data, error: targetError } = await supabase
        .from('nutrition_consultations')
        .select('*')
        .eq('id', existingConsultationTargetId)
        .eq('client_id', client.id)
        .eq('is_deleted', false)
        .maybeSingle();
      if (targetError || !data) {
        setError(targetError?.message || 'Le brouillon exact est introuvable dans le cloud.');
        setConsultation(null);
        setPlanText('');
        setLoadingInitial(false);
        return;
      }
      const exact = normalizeNutritionConsultationCloudRow(data, client.id);
      setError(null);
      setConsultation(exact);
      consultationPersistenceRef.current = exact;
      setPlanText(exact.nutritionPlan);
      setAiDirectives(exact.aiDirectives);
      setLoadingInitial(false);
      return;
    }
    // Rechargement froid : la ligne cloud complète est l'autorité. Un
    // sous-ensemble de colonnes perdrait l'id, la révision CAS ou l'adoption
    // MGD et pourrait rendre publiable un brouillon incomplet.
    const { data, error: latestError } = await supabase
      .from('nutrition_consultations')
      .select('*')
      .eq('client_id', client.id)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (latestError) {
      setError(latestError.message || 'La version cloud exacte du plan est indisponible.');
      setConsultation(null);
      consultationPersistenceRef.current = null;
      setPlanText('');
      setLoadingInitial(false);
      return;
    }
    if (data) {
      const normalized = normalizeNutritionConsultationCloudRow(data, client.id);
      setConsultation(normalized);
      consultationPersistenceRef.current = normalized;
      setPlanText(normalized.nutritionPlan || '');
      setAiDirectives(normalized.aiDirectives || '');
    }
    setLoadingInitial(false);
  }, [
    client?.id,
    existingConsultationTarget,
    existingConsultationTargetId,
  ]);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  useEffect(() => {
    let cancelled = false;
    setMgdCaseState('checking');
    if (!client?.id) return () => { cancelled = true; };
    (async () => {
      try {
        const status = await mgdReviewV2Client.status(client.id);
        if (cancelled) return;
        if (status.reports.length === 0) { setMgdCaseState('none'); return; }
        setMgdCaseState('active');
      } catch {
        if (!cancelled) setMgdCaseState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id]);

  const assertFreshBrowserAiAction = useCallback(async (currentText) => {
    const currentConsultation = consultationPersistenceRef.current || consultation;
    try {
      const authority = await assertBrowserAiActionAuthority({
        clientId: client?.id,
        consultation: currentConsultation,
        planText: currentText,
      });
      setMgdCaseState(authority.status.reports.length > 0 ? 'active' : 'none');
      return authority;
    } catch (caught) {
      if (!currentConsultation?.editorial_overrides?.external_plan_input) {
        setMgdCaseState('error');
      }
      throw caught;
    }
  }, [client?.id, consultation]);

  // ─── Reecriture IA d'une section ─────────────────────────────
  const runRewriteSection = async (sectionIndex, action) => {
    const target = sections[sectionIndex];
    if (!target || target.content.trim().length < 10) {
      setAiError('Cette section est trop courte pour être retravaillée.');
      return;
    }
    setAiBusy({ sectionIndex, action });
    setAiError(null);
    try {
      await assertFreshBrowserAiAction(planText);
      // V97.60 — bloc sécurité de la fiche dans la réécriture : la consigne de
      // réécriture peut introduire des exemples alimentaires ; sans le bloc,
      // rien n'empêche un allergène déclaré d'y apparaître. Fail-safe ('' si
      // fiche vide) ; le filet aval (clairance aux portes) reste en place.
      const sourceForm = client?.form || {};
      const safeForm = buildAnthropicSafeForm(sourceForm);
      const age = safeForm.age;
      const identity = {
        firstName: sourceForm.prenom,
        lastName: sourceForm.nom,
        email: sourceForm.email,
        phone: sourceForm.telephone || sourceForm.phone,
        birthDate: sourceForm.dateNaissance || sourceForm.date_naissance,
        age,
      };
      const safety = buildSafetyBlockFr(safeForm);
      const system = safety ? `${REWRITE_PROMPTS[action]}\n\n${safety}` : REWRITE_PROMPTS[action];
      const res = await callClaude({
        system,
        user: minimizeAnthropicText(target.content, identity),
        model: 'claude-sonnet-5',
        maxTokens: 4000,
      });
      const rawText = typeof res === 'string' ? res : (res?.text || '').trim();
      const newText = rehydrateClientFirstName(rawText, sourceForm.prenom);
      if (!newText) throw new Error('Réponse vide');
      const cleaned = newText.replace(/^"|"$/g, '').trim();
      updateSection(sectionIndex, cleaned);
    } catch (e) {
      setAiError(e?.message || 'Réécriture impossible');
    } finally {
      setAiBusy(null);
    }
  };

  // ─── Audit IA du plan (Phase AP) ─────────────────────────────
  const handleAudit = async () => {
    if (!planText.trim()) {
      setAuditError('Aucun plan à auditer.');
      return;
    }
    setAuditing(true);
    setAuditError(null);
    setAuditResult(null);
    try {
      await assertFreshBrowserAiAction(planText);
      const sourceForm = client.form || {};
      if (!hasRiskProfileAssessment(sourceForm)) {
        throw new Error('Anissa doit confirmer l’évaluation des profils à risque dans l’anamnèse avant la génération.');
      }
      const safeForm = buildAnthropicSafeForm(sourceForm);
      const identity = {
        firstName: sourceForm.prenom,
        lastName: sourceForm.nom,
        email: sourceForm.email,
        phone: sourceForm.telephone || sourceForm.phone,
        birthDate: sourceForm.dateNaissance || sourceForm.date_naissance,
        age: safeForm.age,
      };
      const rawResult = await analyzeFullPlan(
        safeForm,
        minimizeAnthropicText(planText, identity),
        '', // supplementsText : pas séparé dans cette V — inclus dans planText
        {
          locale: 'FR',
          composerProfile: null,
          aiDirectives: minimizeAnthropicText(aiDirectives || '', identity),
        }
      );
      const result = rehydrateClientIdentityValue(rawResult, sourceForm.prenom);
      if (!result) throw new Error('La revue structurée n’a pas pu être produite');
      setAuditResult(result);
    } catch (e) {
      setAuditError(e?.message || 'Revue impossible');
    } finally {
      setAuditing(false);
    }
  };

  // ─── Sauvegarde ──────────────────────────────────────────────
  // V97.27 : autosave + indicateur state extraits dans usePlanAutosave.
  // handleSave reste ici (logique metier specifique). Le hook gere :
  //  - debounce 1.5s sur changement planText
  //  - flash 'saved' 1.8s puis retour 'idle'
  //  - markInitial pour seed lastSavedTextRef au premier load
  const persistEditorConsultation = useCallback(async (draft, options) => {
    if (existingConsultationTargetId
        && (draft?.id !== existingConsultationTargetId
          || (draft?.clientId || draft?.client_id) !== client?.id)) {
      throw new Error('Le brouillon exact a changé : sauvegarde suspendue.');
    }
    await onBeforePersist?.(draft);
    return saveNutritionConsultationConfirmed(draft, options);
  }, [
    client?.id,
    existingConsultationTargetId,
    onBeforePersist,
  ]);

  const handleSave = useCallback(async (textSnapshot, controls = {}) => {
    setError(null);
    const baseConsultation = consultationPersistenceRef.current
      || consultation || { clientId: client.id };
    const requestedEditorialOverrides = controls.meta?.editorialOverrides;
    const next = {
      ...baseConsultation,
      clientId: client.id,
      nutritionPlan: textSnapshot,
      aiDirectives: controls.meta?.aiDirectives ?? aiDirectives,
      createdAt: baseConsultation?.createdAt || new Date().toISOString(),
      status: 'a_valider',
      consultantName: COACH_IDENTITY?.name || 'Anissa',
      editorial_overrides: requestedEditorialOverrides
        ? requestedEditorialOverrides
        : controls.meta?.mealStructure
        ? {
          ...(baseConsultation?.editorial_overrides || {}),
          meal_structure: controls.meta.mealStructure,
        }
        : (baseConsultation?.editorial_overrides || {}),
      meal_recipes: controls.meta?.mealRecipes ?? baseConsultation?.meal_recipes ?? null,
    };
    let authorization = null;
    const adoptionRequest = controls.meta?.kind === 'mgd_adoption'
      ? controls.meta : null;
    const generationProofId = adoptionRequest?.generationProofId
      || mgdGenerationProofIdRef.current;
    const externalInput = baseConsultation?.editorial_overrides?.external_plan_input;
    const externalExpectedRequestId = isExternalPlanInput(externalInput)
      ? externalInput.request_id
      : null;
    const externalSynthesis = resolveExternalDossierSynthesis(client?.journey_state);
    const usesConfirmedExternalMode = Boolean(externalSynthesis)
      || isExternalPlanInput(externalInput);
    if (externalSynthesis) {
      const requestId = globalThis.crypto?.randomUUID?.();
      if (!requestId) throw new Error('Identifiant de sauvegarde externe indisponible.');
      next.nutritionPlan = textSnapshot.replace(/\r\n/g, '\n');
      next.mgdPlanAdoptionId = null;
      next.status = 'a_valider';
      next.editorial_overrides = {
        ...(next.editorial_overrides || {}),
        external_plan_input: {
          plan_input_mode: 'external_anonymized_dossier_v1',
          review_status: 'a_valider',
          privacy_review_confirmed: true,
          synthesis_id: externalSynthesis.synthesis_id,
          request_id: requestId,
          imported_text_sha256: externalSynthesis.synthesis_sha256,
          profile_summary_sha256: externalSynthesis.synthesis_sha256,
          source_profile_summary_sha256: externalSynthesis.profile_summary_sha256,
          current_text_sha256: await sha256Text(next.nutritionPlan),
        },
      };
    } else if (requiresMgdPlanAdoption(mgdCaseState, externalInput)) {
      if (!generationProofId) {
        throw new Error('Le plan MGD a été modifié sans preuve de génération active. Régénère-le avant de sauvegarder.');
      }
      const currentMgdStatus = await mgdReviewV2Client.status(client.id);
      if (!currentMgdStatus.ready || !currentMgdStatus.readinessToken) {
        throw new Error('La revue MGD a changé. Retourne à « Revoir les résultats ». À valider Anissa.');
      }
      try {
        authorization = await mgdReviewV2Client.authorizePlanAdoption({
          clientId: client.id, generationProofId,
          readinessToken: currentMgdStatus.readinessToken, finalPlanText: textSnapshot,
        });
      } catch (caught) { throw new Error(mgdAdoptionErrorMessage(caught)); }
      next.nutritionPlan = textSnapshot.replace(/\r\n/g, '\n');
      next.mgdPlanAdoptionId = authorization.adoptionId;
    } else if (usesConfirmedExternalMode) {
      next.mgdPlanAdoptionId = null;
      next.status = 'a_valider';
      const currentTextSha256 = await sha256Text(next.nutritionPlan);
      next.editorial_overrides = {
        ...(next.editorial_overrides || {}),
        external_plan_input: {
          ...externalInput,
          review_status: 'a_valider',
          request_id: globalThis.crypto?.randomUUID?.(),
          current_text_sha256: currentTextSha256,
        },
      };
    }
    const saved = await persistEditorConsultation(next, externalExpectedRequestId
      ? { externalExpectedRequestId }
      : undefined);
    consultationPersistenceRef.current = saved;
    return { saved, authorization, text: next.nutritionPlan };
  }, [client?.id, client?.journey_state, consultation, aiDirectives, mgdCaseState,
    persistEditorConsultation]);

  const autosaveControl = usePlanAutosave({ planText, loadingInitial, onSave: handleSave,
    onCommit: async (result) => {
      setConsultation(result.saved);
      await awaitConfirmedNutritionReload(result.saved, onPlanSaved);
    },
    onError: (saveError) => setError(saveError?.message || 'Erreur sauvegarde'),
  });
  const autosaveState = autosaveControl.autosaveState;
  const planTextDirty = planText !== String(consultation?.nutritionPlan || '');
  const effectiveAutosaveState = loadingInitial ? 'loading'
    : planTextDirty && autosaveState !== 'saving' ? 'dirty' : autosaveState;

  const saveWeekWorkshopSlot = useCallback(async (draft) => {
    if (!['idle', 'saved'].includes(effectiveAutosaveState)) {
      throw new Error('Attends la fin de la sauvegarde du plan avant de modifier la semaine.');
    }
    setWeekWorkshopSaving(true);
    setWeekWorkshopError(null);
    try {
      const currentConsultation = consultationPersistenceRef.current || consultation;
      if (!currentConsultation?.id) throw new Error('Enregistre le plan avant de modifier la semaine.');
      const currentOverrides = currentConsultation.editorial_overrides || {};
      const mappedPlan = buildClientAppPlanFromConsultation(client, currentConsultation);
      const previousDraft = buildSlotEditorDraft(mappedPlan?.sections?.week_meals, draft?.slot);
      if (!previousDraft) throw new Error('Le créneau d’origine est introuvable. Recharge la semaine.');
      const meals = upsertWeekMealOverride(currentOverrides.meals, draft);
      const mealRecipes = reconcileMealRecipesForSlot(
        currentConsultation.meal_recipes,
        previousDraft,
        draft,
      );
      await autosaveControl.saveNow(planText, {
        kind: 'week_workshop',
        editorialOverrides: { ...currentOverrides, meals },
        mealRecipes,
      });
      setWeekWorkshopSavedAt(Date.now());
    } catch (caught) {
      const message = caught?.message || 'La modification du repas n’a pas été enregistrée.';
      setWeekWorkshopError(message);
      throw caught;
    } finally {
      setWeekWorkshopSaving(false);
    }
  }, [autosaveControl, client, consultation, effectiveAutosaveState, planText]);

  const generateSingleWeekRecipe = useCallback(async (meal) => {
    if (!['idle', 'saved'].includes(effectiveAutosaveState)) {
      setWeekWorkshopError('Attends la fin de la sauvegarde du plan avant de générer une recette.');
      return;
    }
    let request = null;
    try {
      request = buildSingleRecipeRequest(meal);
      setGeneratingRecipeKey(request.key);
      setWeekWorkshopError(null);
      await assertFreshBrowserAiAction(planText);
      const { generateRecipesForMeals } = await import('./services/aiRecipeGenerator');
      const generated = await generateRecipesForMeals([request], {
        form: client?.form || {},
        locale: getClientNutritionLocale(client) || 'fr',
        directives: aiDirectives,
      });
      const currentConsultation = consultationPersistenceRef.current || consultation;
      const mealRecipes = {
        ...(currentConsultation?.meal_recipes || {}),
        ...generated,
      };
      await autosaveControl.saveNow(planText, { kind: 'week_recipe', mealRecipes });
      setWeekWorkshopSavedAt(Date.now());
    } catch (caught) {
      setWeekWorkshopError(caught?.message || 'La recette n’a pas pu être générée.');
    } finally {
      setGeneratingRecipeKey(null);
    }
  }, [aiDirectives, assertFreshBrowserAiAction, autosaveControl, client, consultation,
    effectiveAutosaveState, planText]);

  const saveDetailedRecipes = useCallback(async (mealRecipes) => {
    if (!['idle', 'saved'].includes(effectiveAutosaveState)) {
      throw new Error('Attends la fin de la sauvegarde du plan avant de modifier les recettes.');
    }
    const currentConsultation = consultationPersistenceRef.current || consultation;
    if (!currentConsultation?.id) throw new Error('Enregistre le plan avant de modifier les recettes.');
    const result = await autosaveControl.saveNow(planText, {
      kind: 'detailed_recipes',
      mealRecipes,
    });
    return result?.saved?.meal_recipes || mealRecipes;
  }, [autosaveControl, consultation, effectiveAutosaveState, planText]);

  const generateDetailedRecipes = useCallback(async (meals, currentRecipes) => {
    if (!['idle', 'saved'].includes(effectiveAutosaveState)) {
      throw new Error('Attends la fin de la sauvegarde du plan avant de générer les recettes.');
    }
    await assertFreshBrowserAiAction(planText);
    const { generateRecipesForMeals } = await import('./services/aiRecipeGenerator');
    const generated = await generateRecipesForMeals(meals, {
      form: client?.form || {},
      locale: getClientNutritionLocale(client) || 'fr',
      directives: aiDirectives,
    });
    const mealRecipes = {
      ...(currentRecipes || {}),
      ...generated,
    };
    await saveDetailedRecipes(mealRecipes);
    return mealRecipes;
  }, [aiDirectives, assertFreshBrowserAiAction, client, effectiveAutosaveState,
    planText, saveDetailedRecipes]);

  useEffect(() => {
    onSaveStateChange?.(effectiveAutosaveState);
  }, [effectiveAutosaveState, onSaveStateChange]);

  // Initialise lastSavedTextRef au premier load via hook
  useEffect(() => {
    if (!loadingInitial && planText) {
      autosaveControl.markInitial(planText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInitial]);

  // Sections parsees du plan pour FicheFrigoPreview (composant existant)
  const fridgeSections = useMemo(() => {
    if (!planText) return [];
    try {
      return structurePlanSections(planText, '', { isFollowup: false, locale: 'FR' });
    } catch {
      return [];
    }
  }, [planText]);

  const weekWorkshopPlanState = useMemo(() => {
    const currentConsultation = consultation || null;
    if (!currentConsultation?.id || !currentConsultation?.nutritionPlan?.trim()) {
      return { plan: null, error: null };
    }
    try {
      return {
        plan: buildClientAppPlanFromConsultation(client, currentConsultation),
        error: null,
      };
    } catch (caught) {
      return { plan: null, error: caught?.message || 'La semaine cliente ne peut pas être préparée.' };
    }
  }, [client, consultation]);
  const recipeAuthoringMeals = useMemo(
    () => buildRecipeAuthoringMeals(weekWorkshopPlanState.plan?.sections?.week_meals),
    [weekWorkshopPlanState.plan?.sections?.week_meals],
  );

  if (loadingInitial) {
    return <div style={{ color: 'var(--jrn-text-muted)' }}>Chargement du plan…</div>;
  }

  const hasPlan = planText.trim().length > 0;
  const hasReadyExternalSynthesis = Boolean(
    resolveExternalDossierSynthesis(client?.journey_state),
  );
  const externalInputForAi = consultation?.editorial_overrides?.external_plan_input;
  const hasExternalAiCandidate = isExternalPlanInput(externalInputForAi);
  const mgdClientAiDisabled = mgdCaseState !== 'none' && !hasExternalAiCandidate;
  const planSavePending = effectiveAutosaveState === 'dirty'
    || effectiveAutosaveState === 'saving';

  return (
    <div>
      {/* ─── Tabs ──────────────────────────────────────────────── */}
      <div className="jpe-tabs">
        {allowedTabs.includes('plan') && (
          <button onClick={() => setTab('plan')} className={`jpe-tab ${tab === 'plan' ? 'jpe-tab--active' : ''}`}>
            Plan nutritionnel
          </button>
        )}
        {allowedTabs.includes('week') && (
          <button onClick={() => setTab('week')} className={`jpe-tab ${tab === 'week' ? 'jpe-tab--active' : ''}`} disabled={!hasPlan}>
            Semaine cliente
          </button>
        )}
        {allowedTabs.includes('recipes') && (
          <button onClick={() => setTab('recipes')} className={`jpe-tab ${tab === 'recipes' ? 'jpe-tab--active' : ''}`} disabled={!hasPlan}>
            Recettes
          </button>
        )}
        {allowedTabs.includes('fridge') && (
          <button onClick={() => setTab('fridge')} className={`jpe-tab ${tab === 'fridge' ? 'jpe-tab--active' : ''}`} disabled={!hasPlan}>
            Fiche frigo
          </button>
        )}
      </div>

      {menusRecipesOnly && (
        <p className="jrn-step-intro" role="status" style={{ marginTop: 'var(--jrn-3)' }}>
          Cette version est limitée aux menus et recettes conformément à la portée validée.
        </p>
      )}

      {/* ─── Tab : Plan ────────────────────────────────────────── */}
      {allowedTabs.includes('plan') && tab === 'plan' && (
        <>
          {/* BC.5G.2 : barre d'actions visible uniquement si plan existe.
              Quand pas de plan, le CTA 'Générer' est intégré dans l'empty state
              (un seul vrai bouton primaire, pas de doublon visuel). */}
          {hasPlan && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--jrn-3)',
              marginTop: 'var(--jrn-5)',
              marginBottom: 'var(--jrn-6)',
              flexWrap: 'wrap',
            }}>
              {canRegenerateFullPlan && (
                <button onClick={() => setShowGenModal(true)} disabled={planSavePending}
                  className="jrn-btn jrn-btn--primary">
                  ✨ Régénérer le plan
                </button>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--jrn-3)', flexWrap: 'wrap' }}>
                <AutosaveIndicator state={autosaveState} />
                <button onClick={handleAudit} disabled={auditing || mgdClientAiDisabled}
                  aria-describedby={mgdClientAiDisabled ? 'mgd-client-ai-disabled' : undefined}
                  className="jrn-btn jrn-btn--soft" title="Revue qualité du plan (cohérence, oublis, contre-indications)">
                  {auditing ? '🔎 Audit…' : '🔎 Audit qualité'}
                </button>
                <button onClick={() => setShowPreviewModal(true)} className="jrn-btn jrn-btn--ghost">
                  Aperçu
                </button>
              </div>
              {mgdClientAiDisabled && <span id="mgd-client-ai-disabled" className="sr-only">
                Pour cette ancienne version du dossier, l’audit et les réécritures sont désactivés.
              </span>}
            </div>
          )}

          {!hasPlan && (
            <div className="jrn-plan-empty" id="journey-first-plan">
              <div className="jrn-plan-empty__icon">✦</div>
              <span className="jrn-plan-empty__version">Version initiale · non générée</span>
              <h4 className="jrn-plan-empty__title">Aucun brouillon pour le moment</h4>
              {hasReadyExternalSynthesis && (
                <p className="jrn-result-pill jrn-result-pill--optimal" role="status">
                  ✓ La synthèse est prête
                </p>
              )}
              <p className="jrn-step-intro" style={{ marginTop: 'var(--jrn-3)' }}>
                Choisis la structure des repas, génère le brouillon, relis-le, puis adopte le plan.
                Le bloc 2 · Validation s’ouvrira après son enregistrement.
              </p>

              <div className="jrn-plan-empty__cols">
                {/* Colonne sources (input) */}
                <div className="jrn-plan-empty__col">
                  <p className="jrn-plan-empty__col-label">La préparation utilisera</p>
                  <ul className="jrn-plan-empty__sources">
                    <li><span className="jrn-plan-empty__check">✓</span><strong>Anamnèse</strong></li>
                    <li><span className="jrn-plan-empty__check">✓</span><strong>Résultats autorisés et validés par Anissa</strong></li>
                    <li><span className="jrn-plan-empty__check">✓</span><strong>Axes prioritaires</strong></li>
                    <li><span className="jrn-plan-empty__check">✓</span><strong>Directives d’Anissa</strong></li>
                  </ul>
                </div>

                {/* Colonne outputs (preview du brouillon) */}
                <div className="jrn-plan-empty__col">
                  <p className="jrn-plan-empty__col-label">Le brouillon contiendra</p>
                  <ul className="jrn-plan-empty__outputs">
                    <li><span className="jrn-plan-empty__dot">✦</span>Structure des repas</li>
                    <li><span className="jrn-plan-empty__dot">✦</span>Timing nutritionnel</li>
                    <li><span className="jrn-plan-empty__dot">✦</span>Supplémentation suggérée</li>
                    <li><span className="jrn-plan-empty__dot">✦</span>Recommandations lifestyle</li>
                    <li><span className="jrn-plan-empty__dot">✦</span>Fiches pratiques</li>
                  </ul>
                </div>
              </div>

              {canRegenerateFullPlan && (
                <button onClick={() => setShowGenModal(true)} disabled={planSavePending}
                  className="jrn-btn jrn-btn--hero jrn-plan-empty__cta">
                  Continuer : préparer le premier plan →
                </button>
              )}
            </div>
          )}

          {hasPlan && (
            <>
              {aiError && <div className="jrn-error" style={{ marginBottom: 'var(--jrn-3)' }}>⚠ {aiError}</div>}

              {/* Liste de sections editables */}
              <div className="jpe-sections">
                {sections.map((section, i) => (
                  <PlanSection
                    key={i}
                    index={i}
                    title={section.title}
                    content={section.content}
                    onTitleChange={(v) => updateSectionTitle(i, v)}
                    onContentChange={(v) => updateSection(i, v)}
                    onDelete={() => removeSection(i)}
                    onInsertAfter={() => insertSectionAfter(i)}
                    onAi={(action) => runRewriteSection(i, action)}
                    busyAction={aiBusy?.sectionIndex === i ? aiBusy.action : null}
                    aiDisabled={mgdClientAiDisabled}
                  />
                ))}
                {sections.length === 0 && (
                  <div className="jrn-surface jrn-surface--quiet" style={{ textAlign: 'center', padding: 'var(--jrn-8)' }}>
                    <p style={{ margin: 0, color: 'var(--jrn-text-muted)' }}>
                      Le plan n'a pas de sections (titres ##). Le contenu brut :
                    </p>
                    <textarea
                      value={planText}
                      onChange={(e) => setPlanText(e.target.value)}
                      rows={20}
                      className="jrn-textarea"
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, marginTop: 'var(--jrn-3)' }}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* « Document résultats » — livrable Word DÉDIÉ, distinct du plan.
              Le collage anticipé reste disponible, avec un retour explicite
              vers la préparation du premier plan tant qu'il n'est pas sauvé. */}
          {resultsExplanationClient && (
            <ResultsExplanationTab
              client={resultsExplanationClient}
              consultation={consultation}
              hasPlan={hasPlan}
            />
          )}
        </>
      )}

      {allowedTabs.includes('week') && tab === 'week' && hasPlan && (
        <ClientWeekWorkshop
          weekMeals={weekWorkshopPlanState.plan?.sections?.week_meals}
          saving={weekWorkshopSaving}
          savedAt={weekWorkshopSavedAt}
          error={weekWorkshopError || weekWorkshopPlanState.error}
          disabled={!['idle', 'saved'].includes(effectiveAutosaveState)}
          generatingRecipeKey={generatingRecipeKey}
          onSaveSlot={saveWeekWorkshopSlot}
          onGenerateRecipe={mgdClientAiDisabled ? undefined : generateSingleWeekRecipe}
          onOpenClientPreview={onOpenClientPreview}
        />
      )}

      {allowedTabs.includes('recipes') && tab === 'recipes' && hasPlan && (
        <div style={{ marginTop: 'var(--jrn-5)' }}>
          <p className="jrn-step-intro" style={{ marginTop: 0, marginBottom: 'var(--jrn-4)' }}>
            Les recettes de la version précédente sont conservées. Modifie-les ici ou régénère uniquement celle qui doit changer, puis enregistre ce brouillon.
          </p>
          <RecipesTab
            consultation={{
              ...(consultation || {}),
              nutritionPlan: planText,
              nutrition_plan: planText,
            }}
            form={client?.form || {}}
            meals={recipeAuthoringMeals}
            onSave={saveDetailedRecipes}
            onGenerateAll={(meals, currentRecipes) => generateDetailedRecipes(meals, currentRecipes)}
            onGenerateRecipe={(meal, currentRecipes) => generateDetailedRecipes([meal], currentRecipes)}
            disabled={!['idle', 'saved'].includes(effectiveAutosaveState)}
            saving={effectiveAutosaveState === 'saving'}
          />
        </div>
      )}

      {/* ─── Tab : Fiche frigo ─────────────────────────────────── */}
      {allowedTabs.includes('fridge') && tab === 'fridge' && (
        <div style={{ marginTop: 'var(--jrn-5)' }}>
          {!hasPlan ? (
            <div className="jrn-surface jrn-surface--quiet" style={{ textAlign: 'center', padding: 'var(--jrn-10)' }}>
              <p style={{ margin: 0, color: 'var(--jrn-text-muted)' }}>
                Générez d'abord un plan nutritionnel pour construire la fiche frigo.
              </p>
            </div>
          ) : (
            <>
              <p className="jrn-step-intro" style={{ marginTop: 0, marginBottom: 'var(--jrn-4)' }}>
                Fiche frigo extraite automatiquement du plan. Cliquez ci-dessous pour ouvrir l'éditeur complet (3 vues : aperçu, édition, vue cliente) et l'export PDF.
              </p>
              <div className="jrn-actions" style={{ marginTop: 0 }}>
                <button onClick={() => setShowFridgeModal(true)} className="jrn-btn jrn-btn--primary">
                  Ouvrir l'éditeur fiche frigo
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {error && <div className="jrn-error">⚠ {error}</div>}

      {/* ─── Modals ────────────────────────────────────────────── */}
      {canRegenerateFullPlan && showGenModal && (
        <GenerationModal
          client={client}
          consultation={consultation}
          aiDirectives={aiDirectives}
          externalSynthesis={resolveExternalDossierSynthesis(client?.journey_state)}
          onDirectivesChange={setAiDirectives}
          onCancel={() => setShowGenModal(false)}
          onAdopt={async (text, updatedDirectives, selectedMealStructure, mgdGenerationProof) => {
            // V97.3 Phase C1 : tracking adoption plan IA (source='ai')
            // Mesure beforeLength = planText AVANT (0 si première génération,
            // ou ancien plan si régénération). afterLength = plan adopté.
            trackPlanModification({
              clientId: client?.id,
              consultationId: consultation?.id,
              section: 'global',
              source: 'ai',
              beforeLength: planText?.length || 0,
              afterLength: text?.length || 0,
            });
            setError(null);
            try {
              mgdGenerationProofIdRef.current = mgdGenerationProof?.id || null;
              const result = mgdGenerationProof
                ? await autosaveControl.saveNow(text, {
                  kind: 'mgd_adoption', generationProofId: mgdGenerationProof.id,
                  aiDirectives: updatedDirectives, mealStructure: selectedMealStructure,
                })
                : await autosaveControl.saveNow(text, { kind: 'explicit_adoption',
                  aiDirectives: updatedDirectives, mealStructure: selectedMealStructure });
              const savedConsultation = result.saved;
              consultationPersistenceRef.current = savedConsultation;
              setAiDirectives(updatedDirectives);
              setPlanText(result.text || text);
              setShowGenModal(false);
            } catch (saveError) {
              setError(saveError?.message || 'Erreur sauvegarde');
              autosaveControl.markError();
              throw saveError;
            }
          }}
        />
      )}
      {showPreviewModal && (
        <PreviewModal text={planText} onClose={() => setShowPreviewModal(false)} />
      )}
      {(auditResult || auditError) && (
        <AuditModal
          result={auditResult}
          error={auditError}
          onClose={() => { setAuditResult(null); setAuditError(null); }}
        />
      )}
      {showFridgeModal && (
        <FicheFrigoPreview
          consultation={{
            ...(consultation || {}),
            nutritionPlan: planText,
            ficheFrigoJson: consultation?.ficheFrigoJson || consultation?.fiche_frigo_json || null,
            date: consultation?.createdAt || new Date().toISOString(),
          }}
          sections={fridgeSections}
          client={client}
          onClose={() => setShowFridgeModal(false)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Modal de génération (plein écran lisible)
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// AuditModal — Affiche le résultat de l'audit IA (Phase AP)
// ═══════════════════════════════════════════════════════════════════

function AuditModal({ result, error, onClose }) {
  const score = result?.score ?? null;
  const verdict = result?.verdict || result?.summary || '';
  const quickWins = Array.isArray(result?.quickWins) ? result.quickWins : (Array.isArray(result?.quick_wins) ? result.quick_wins : []);
  const strengths = Array.isArray(result?.strengths) ? result.strengths : [];
  const issues = Array.isArray(result?.issues) ? result.issues : [];
  const panelRef = useRef(null);
  useModalA11y({ onClose, panelRef });

  const scoreColor = score == null
    ? 'var(--jrn-text-muted)'
    : score >= 80
      ? 'var(--jrn-accent)'
      : score >= 60
        ? '#8a6722'
        : 'var(--jrn-error)';

  return (
    <div className="jpe-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panelRef} className="jpe-modal jpe-modal--xl" role="dialog"
        aria-modal="true" aria-labelledby="plan-audit-title" tabIndex={-1}>
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">🔎 Audit qualité du plan</p>
            <h3 id="plan-audit-title" className="jpe-modal__title">Revue interne du plan</h3>
          </div>
          <button onClick={onClose} className="jrn-btn jrn-btn--ghost">Fermer</button>
        </header>

        <div className="jpe-modal__body">
          {error && (
            <div className="jrn-surface jrn-surface--warn" role="alert">
              <div className="jrn-label">Erreur</div>
              <p style={{ margin: '4px 0 0', color: 'var(--jrn-text)' }}>{error}</p>
            </div>
          )}

          {result && (
            <>
              {/* Score global */}
              {score != null && (
                <div className="jrn-surface" style={{ marginBottom: 'var(--jrn-5)', display: 'flex', alignItems: 'center', gap: 'var(--jrn-5)' }}>
                  <div style={{
                    fontFamily: 'var(--jrn-font-display)',
                    fontStyle: 'italic',
                    fontSize: 56,
                    fontWeight: 600,
                    color: scoreColor,
                    lineHeight: 1,
                  }}>
                    {score}<span style={{ fontSize: 20, color: 'var(--jrn-text-muted)', fontStyle: 'normal' }}>/100</span>
                  </div>
                  {verdict && (
                    <div style={{ flex: 1, fontSize: 14, color: 'var(--jrn-text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {verdict}
                    </div>
                  )}
                </div>
              )}

              {/* Forces */}
              {strengths.length > 0 && (
                <div style={{ marginBottom: 'var(--jrn-5)' }}>
                  <div className="jrn-label" style={{ color: 'var(--jrn-accent)' }}>✓ Points forts ({strengths.length})</div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: 'var(--jrn-text)' }}>
                    {strengths.map((s, i) => <li key={i}>{typeof s === 'string' ? s : s.text || s.message || JSON.stringify(s)}</li>)}
                  </ul>
                </div>
              )}

              {/* Issues */}
              {issues.length > 0 && (
                <div style={{ marginBottom: 'var(--jrn-5)' }}>
                  <div className="jrn-label" style={{ color: '#b53a3a' }}>⚠ Points à améliorer ({issues.length})</div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: 'var(--jrn-text)' }}>
                    {issues.map((issue, i) => {
                      const text = typeof issue === 'string' ? issue : (issue.text || issue.message || issue.description || JSON.stringify(issue));
                      const severity = issue?.severity || issue?.priority;
                      return (
                        <li key={i}>
                          {text}
                          {severity && (
                            <span style={{
                              marginLeft: 6,
                              fontSize: 9,
                              padding: '1px 6px',
                              borderRadius: 4,
                              background: 'rgba(181, 58, 58, 0.1)',
                              color: '#b53a3a',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}>
                              {severity}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Quick wins */}
              {quickWins.length > 0 && (
                <div style={{ marginBottom: 'var(--jrn-5)' }}>
                  <div className="jrn-label" style={{ color: '#7e5ec7' }}>✨ Améliorations suggérées ({quickWins.length})</div>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: 'var(--jrn-text)' }}>
                    {quickWins.map((q, i) => <li key={i}>{typeof q === 'string' ? q : q.text || q.action || q.message || JSON.stringify(q)}</li>)}
                  </ul>
                </div>
              )}

              <p style={{ fontSize: 11, color: 'var(--jrn-text-muted)', marginTop: 'var(--jrn-5)', fontStyle: 'italic' }}>
                Cet audit est un assistant qualité interne. Anissa garde le dernier mot sur le contenu du plan.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Indicateur d'auto-save discret (4 états)
// ═══════════════════════════════════════════════════════════════════

function AutosaveIndicator({ state }) {
  const map = {
    idle:   { label: 'Auto-sauvegarde activée',          color: 'var(--jrn-text-muted)' },
    dirty:  { label: '● Modifications…',                  color: 'var(--jrn-warn)' },
    saving: { label: '⟳ Sauvegarde…',                     color: 'var(--jrn-text-muted)' },
    saved:  { label: '✓ Sauvegardé',                      color: 'var(--jrn-accent)' },
    error:  { label: '⚠ Erreur — réessayer',              color: 'var(--jrn-error)' },
  };
  const cur = map[state] || map.idle;
  return (
    <span style={{
      fontFamily: 'var(--jrn-font-ui)',
      fontSize: 'var(--jrn-text-xs)',
      color: cur.color,
      letterSpacing: '.02em',
      whiteSpace: 'nowrap',
    }}>
      {cur.label}
    </span>
  );
}

function GenerationModal({ client, consultation, aiDirectives, externalSynthesis = null,
  onDirectivesChange, onCancel, onAdopt }) {
  // BC.5G.6 : la directive est éditée dans la sidebar atelier (source unique).
  // À l'ouverture, on refetch la dernière directive en BDD pour être à jour
  // (la sidebar peut avoir été modifiée après le mount de JourneyPlanEditor).
  const [draftDirectives, setDraftDirectives] = useState(aiDirectives || '');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [mgdStatus, setMgdStatus] = useState(null);
  const [mgdStatusState, setMgdStatusState] = useState('loading');
  const [mgdGenerationProof, setMgdGenerationProof] = useState(null);
  const [mgdReadinessToken, setMgdReadinessToken] = useState(null);
  const [adopting, setAdopting] = useState(false);
  const adoptingRef = useRef(false);
  const generationAttemptRef = useRef(null);
  const panelRef = useRef(null);
  useModalA11y({ onClose: onCancel, busy: generating || adopting, panelRef });

  const refreshMgdStatus = useCallback(async () => {
    if (!client?.id) return null;
    setMgdStatusState('loading');
    try {
      const next = await mgdReviewV2Client.status(client.id);
      setMgdStatus(next); setMgdStatusState('ready');
      return next;
    } catch {
      setMgdStatus(null); setMgdStatusState('error');
      return null;
    }
  }, [client?.id]);

  useEffect(() => { refreshMgdStatus(); }, [refreshMgdStatus]);

  const assertFreshBrowserAiAction = useCallback(async (currentText) => (
    assertBrowserAiActionAuthority({
      clientId: client?.id,
      consultation,
      planText: currentText,
    })
  ), [client?.id, consultation]);

  // V97.13.x : la prop `aiDirectives` vient désormais de la sidebar parent
  // (ClientJourneyPage → JourneyPlanEditor via controlledAiDirectives).
  // On re-fetch DB UNIQUEMENT si la prop est vide (fallback pour cas où le parent
  // n'a pas encore chargé). Sinon, la prop fait foi pour éviter d'écraser une
  // directive fraîchement tapée et pas encore persistée (race autosave 250ms).
  useEffect(() => {
    if (!client?.id) return;
    if (aiDirectives && aiDirectives.trim()) return; // prop a priorité
    let cancelled = false;
    (async () => {
      try {
        const { getNutritionConsultations } = await import('./store');
        const list = getNutritionConsultations(client.id) || [];
        const last = list[0];
        if (!cancelled && last?.aiDirectives) {
          setDraftDirectives(last.aiDirectives);
        }
      } catch (e) {
        // La directive IA reste vide : l'atelier fonctionne, mais Anissa
        // croirait n'en avoir jamais écrit. Au moins laisser une trace.
        console.warn('[atelier] directive IA précédente illisible :', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [client?.id, aiDirectives]);

  // V97.13.x : si la prop change pendant que le modal est ouvert (peu probable
  // mais possible si l'utilisateur tape encore via un autre tab), on re-sync.
  useEffect(() => {
    if (aiDirectives && aiDirectives !== draftDirectives) {
      setDraftDirectives(aiDirectives);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDirectives]);
  // Composition profil-aware validée : moteur unique, toujours actif.
  const [detectedProfile, setDetectedProfile] = useState(null);
  // La structure des repas est un choix explicite d'Anissa. Aucun signal du
  // questionnaire ne présélectionne une option. Le choix validé est conservé
  // avec la consultation, jamais seulement dans le navigateur.
  const persistedMealStructure = consultation?.editorial_overrides?.meal_structure;
  const [mealStructure, setMealStructure] = useState(
    MEAL_STRUCTURES.some((item) => item.id === persistedMealStructure)
      ? persistedMealStructure
      : '',
  );
  const generationLaunchGate = resolveGenerationLaunchGate({
    externalSynthesis,
    mealStructure,
    mgdStatus,
    mgdStatusState,
  });
  // V97.27 (refacto) — Hook guardrails : state + preload DB + ref synchrone
  // pour audit post-gen (preserve fix race condition V97.25 HIGH-2).
  const guardrails = useGuardrailsAudit({ supabase });
  // V97.20 (OBS-1) — id de la row d'observability associee a la generation courante.
  // null tant que pas encore enregistree. Utilise pour increment compteurs slop.
  const [observabilityId, setObservabilityId] = useState(null);
  // V97.27 (refacto) — Hook anti-slop : remplace 4 useState + 4 handlers
  // inline qui faisaient ~120 lignes.
  const slop = useSlopAudit({ observabilityId, form: client?.form || {} });

  // V97.27 — Preload guardrails DB gere par useGuardrailsAudit hook.

  // Barre de progression estimee : asymptote vers 92% sur ~60s, puis 100% a l'arrivee.
  // L'API Claude ne stream pas la progression reelle ; on simule pour donner du feedback.
  useEffect(() => {
    if (!generating) return;
    setProgress(0);
    setElapsed(0);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const sec = (Date.now() - startTime) / 1000;
      setElapsed(sec);
      // Courbe : 0 → 92% en ~80s, asymptotique
      setProgress(92 * (1 - Math.exp(-sec / 28)));
    }, 250);
    return () => clearInterval(interval);
  }, [generating]);

  const handleGenerate = async () => {
    if (!MEAL_STRUCTURES.some((item) => item.id === mealStructure)) {
      setError('Choisis la structure des repas avant de lancer la génération.');
      return;
    }
    if (getClientNutritionLocale(client) === 'EN') {
      setError('Le moteur profil-aware anglais n\u2019est pas encore valid\u00e9. La g\u00e9n\u00e9ration reste indisponible pour cette cliente.');
      return;
    }
    const planMode = getNutritionPlanMode(client);
    if (planMode !== 'oneshot' && planMode !== 'followup') {
      setError('Pack absent ou inconnu : génération du plan indisponible.');
      return;
    }
    setGenerating(true);
    setError(null);
    setResult(null);
    setMgdGenerationProof(null);
    setMgdReadinessToken(null);
    setDetectedProfile(null);
    guardrails.reset();
    slop.reset();
    setObservabilityId(null);
    // V97.3 Phase B : timer + capture system prompt pour observabilité
    const startedAt = Date.now();
    const MODEL = 'claude-sonnet-5';
    let systemForTracking = null;
    // V97.20 (OBS-1) — capture locale du profile (state async pas dispo a temps)
    let profileForObs = null;
    try {
      const sourceForm = client.form || {};
      // Le moteur reçoit l'âge utile, jamais le prénom réel ni la date de
      // naissance exacte. Le form source reste local pour les données métier.
      const form = buildAnthropicSafeForm(sourceForm);

      // V97.18 Phase D — Extrait la phase active du parcours pour injecter
      // ses recommandations cliniques (foods_favor/limit, supplements, etc.)
      // dans le composer. Source : consultation.protocol_phases ou client.protocol_phases.
      // Best-effort : null si pas de parcours active (legacy ou pas encore demarre).
      // Nouveau suivi : la phase peut avoir été activée par une remise PDF.
      const datesActivation = await lireDatesActivationPhasesVite(client?.id);
      const { activePhase } = resolveProtocolPhaseForGeneration(consultation, client, { datesActivation });

      const opts = {
        isFollowup: false,
        clientFormule: client.formule || 'nutrition',
        followupWeek: 0,
        planMode,
        activePhase,
      };

      // Le clinicalContext est construit localement puis minimisé avant toute
      // transmission. Une erreur ferme la génération au lieu de retirer
      // silencieusement une partie du contexte clinique.
      const identity = {
        firstName: sourceForm.prenom,
        lastName: sourceForm.nom,
        email: sourceForm.email,
        phone: sourceForm.telephone || sourceForm.phone,
        birthDate: sourceForm.dateNaissance || sourceForm.date_naissance,
        age: form.age,
      };
      // Toujours relire l'autorité serveur au moment exact de la génération.
      // Une panne interdit de supposer qu'aucun rapport MGD n'existe.
      let exactExternalSynthesis = '';
      if (externalSynthesis) {
        const authoritativeSynthesis = await readExternalDossierSynthesisForGeneration({
          clientId: client.id,
          synthesisId: externalSynthesis.synthesis_id,
          expectedSynthesisSha256: externalSynthesis.synthesis_sha256,
        });
        if (authoritativeSynthesis.profileSummarySha256
            !== externalSynthesis.profile_summary_sha256) {
          throw new Error('La preuve serveur ne correspond pas au profil relu. Recoller la synthèse.');
        }
        exactExternalSynthesis = authoritativeSynthesis.text;
      }
      const usesExternalSynthesis = Boolean(exactExternalSynthesis);
      const currentMgdStatus = await mgdReviewV2Client.status(client.id);
      setMgdStatus(currentMgdStatus); setMgdStatusState('ready');
      const hasActiveMgdReports = !usesExternalSynthesis && currentMgdStatus.reports.length > 0;
      if (hasActiveMgdReports && (!currentMgdStatus.ready || !currentMgdStatus.readinessToken
          || !currentMgdStatus.revisionHash)) {
        const messages = {
          safety_context_missing: 'Confirme dans la fiche cliente : grossesse, allaitement, post-partum et évaluation des profils à risque. À valider Anissa.',
          reports_unprepared: 'Prépare chaque rapport MGD dans « Revoir les résultats » avant la génération.',
          review_incomplete: 'Finalise les décisions Anissa sur chaque rapport MGD avant la génération.',
          anomalies_unresolved: 'Des anomalies techniques MGD restent à vérifier. La génération est suspendue.',
          conflicts_unresolved: 'Des données MGD en conflit restent à trancher. La génération est suspendue.',
          programme_conflict_unresolved: 'Un conflit entre résultats MGD destinés au programme reste à trancher. La génération est suspendue.',
          revision_stale: 'La revue MGD n’est plus à jour. Retourne à « Revoir les résultats ».',
        };
        const readinessError = new Error(messages[currentMgdStatus.code]
          || 'La revue MGD n’est pas prête. À valider Anissa.');
        readinessError.code = 'mgd_review_not_ready';
        throw readinessError;
      }
      if (hasActiveMgdReports) {
        const loadedPolicy = await preloadClinicalPolicyRegistryFromSupabase(supabase, { force: true });
        assertMgdClinicalPolicyReady(loadedPolicy.manifest);
      }
      // Pour MGD, le serveur relit seul l'anamnèse minimisée, les directives,
      // phases et valeurs validées. Le navigateur ne construit aucun prompt.
      const clinicalContext = hasActiveMgdReports ? null : minimizeAnthropicValue(buildClinicalContext({
        journeyState: client?.journey_state, form: sourceForm, validatedLabResults: [],
      }), identity);
      const v2 = hasActiveMgdReports ? null
        : buildSystemPromptFrV2(form, opts, { useComposer: true, clinicalContext });
      if (v2?.blocked) {
        const motifs = v2.motifs || [];
        const reason = motifs.length
          ? motifs.map((m) => m.libelle).join(' + ')
          : (v2.profile?.blockReason || 'profil non supporté');
        trackPlanGenerationFailed({
          clientId: client?.id,
          model: MODEL,
          durationMs: Date.now() - startedAt,
          errorType: 'clinical_blocked',
          errorMessageSafe: `blocage clinique: ${reason}`.slice(0, 200),
          composerBeta: true,
        });
        const blockedError = new Error(
          motifs.length
            ? formatBlocages(motifs)
            : `Composition profil-aware : profil "${reason}" pas encore supporté par les modules cliniques.`,
        );
        blockedError.code = 'clinical_blocked';
        throw blockedError;
      }
      if (v2 && activePhase && !v2.phaseRecommendations) {
        const phaseError = new Error(
          'Les recommandations publiées de la phase active sont indisponibles. La génération est suspendue pour éviter un plan incomplet.',
        );
        phaseError.code = 'phase_recommendations_unavailable';
        throw phaseError;
      }
      let system = v2?.prompt || null;
      setDetectedProfile(v2?.profile || null);
      profileForObs = v2?.profile || null;
      // V97.27 — Stocker les guardrails actifs via le hook (audit post-gen).
      if (v2) guardrails.setActiveGuardrails(v2.guardrails);
      // 2026-07-26 — Structure des repas choisie au cadrage : bloc ajouté EN
      // FIN de prompt pour primer sur
      // les mentions historiques « 3 repas + 1 collation » codées en dur.
      const mealBlock = buildMealStructureConstraintFr(mealStructure);
      if (mealBlock && system) system = `${system}\n\n${mealBlock}`;
      systemForTracking = system;

      const user = hasActiveMgdReports ? null
        : buildMinimalUserMessage(client, sourceForm, draftDirectives, new Date(),
          exactExternalSynthesis);
      let sealedDraft = null;
      if (hasActiveMgdReports) {
        const attemptKey = `${currentMgdStatus.revisionHash}:${mealStructure}:${consultation?.id || 'initial'}`;
        if (generationAttemptRef.current?.key !== attemptKey) {
          generationAttemptRef.current = { key: attemptKey,
            draftRequestId: mgdReviewV2Client.createRequestId(),
            generationRequestId: mgdReviewV2Client.createRequestId(), draft: null };
        }
        const attempt = generationAttemptRef.current;
        sealedDraft = attempt.draft || await mgdReviewV2Client.createPlanDraft({
          clientId: client.id, consultationId: consultation?.id || null,
          readinessToken: currentMgdStatus.readinessToken,
          requestId: attempt.draftRequestId, mealStructure,
        });
        attempt.draft = sealedDraft;
      }
      const gatedGeneration = hasActiveMgdReports
        ? await mgdReviewV2Client.generatePlan({ clientId: client.id,
          draftId: sealedDraft.draftId,
          readinessToken: currentMgdStatus.readinessToken,
          requestId: generationAttemptRef.current.generationRequestId })
        : null;
      const res = hasActiveMgdReports
        ? gatedGeneration.text
        : await callClaude({
          system,
          user,
          model: MODEL,
          maxTokens: 32000, // V97.128 — 16k tronquait les plans profil-aware (4 sem. + modules cliniques). Sonnet 5 supporte largement.
          // V97.109 — plan complet Sonnet 5 : peut dépasser 2 min. On laisse 5 min
          // (aligné sur maxDuration=300 du proxy) au lieu du défaut 120s qui coupait.
          timeoutMs: 300000,
        });
      setProgress(100);
      await new Promise((r) => setTimeout(r, 350));
      const rawResponseText = typeof res === 'string' ? res : res?.text || JSON.stringify(res);
      // La substitution d'affichage a lieu avant l'autorisation finale. Le
      // serveur hash ensuite ce texte exact et lie l'adoption one-shot.
      const responseText = (hasActiveMgdReports ? rawResponseText
        : rehydrateClientFirstName(rawResponseText, sourceForm.prenom)).replace(/\r\n/g, '\n');
      // V97.108 — filet : ne jamais retomber sur un "rien ne s'affiche" silencieux.
      // Si l'IA renvoie une réponse vide, on lève une erreur visible.
      if (!responseText || !responseText.trim()) {
        throw new Error("La préparation a renvoyé une réponse vide. Relancez la génération.");
      }
      setResult(responseText);
      if (hasActiveMgdReports) {
        setMgdGenerationProof(gatedGeneration.generationProof);
        setMgdReadinessToken(currentMgdStatus.readinessToken);
        generationAttemptRef.current = null;
      }
      onDirectivesChange?.(draftDirectives);

      // V97.27 — Audit clinique post-generation via hook (preserve fix
      // race condition V97.25 HIGH-2 grace au ref interne au hook).
      const guardrailAudit = hasActiveMgdReports
        ? { violations: [], completeness: null, guardrails: [] }
        : guardrails.runAudit(responseText);
      const { violations: auditViolations, completeness: auditCompleteness,
        guardrails: activeGuardrails } = guardrailAudit;

      // V97.20 (OBS-1) — Record generation observability (best-effort, non-bloquant)
      recordPlanGeneration({
        clientId: client?.id,
        generationDurationMs: Date.now() - startedAt,
        model: MODEL,
        composerBeta: true,
        profile: profileForObs,
        guardrails: activeGuardrails,
        violations: auditViolations,
        completeness: auditCompleteness,
        slopFlags: null, // pas encore audite
        planText: responseText,
      }).then((res) => {
        if (res.ok && res.id) setObservabilityId(res.id);
      });

      // V97.3 Phase B : tracking succès (best-effort, non bloquant)
      trackPlanGenerated({
        clientId: client?.id,
        model: MODEL,
        durationMs: Date.now() - startedAt,
        systemPrompt: systemForTracking,
        responseText,
        composerBeta: true,
        sectionsGenerated: ['plan'],
      });
    } catch (e) {
      setError(e?.message || 'Génération impossible');
      // V97.3 Phase B : tracking échec (sauf composer_blocked déjà tracké au-dessus)
      const msg = e?.message || '';
      if (e?.code !== 'clinical_blocked') {
        const status = e?.status;
        let errorType = 'api_error';
        if (status === 429 || /rate.?limit/i.test(msg)) errorType = 'rate_limit';
        else if (status === 0 || /reseau|network|fetch/i.test(msg)) errorType = 'network_error';
        else if (/timeout|aborted/i.test(msg)) errorType = 'timeout';
        else if (status && status >= 500) errorType = 'server_error';
        trackPlanGenerationFailed({
          clientId: client?.id,
          model: MODEL,
          durationMs: Date.now() - startedAt,
          errorType,
          errorMessageSafe: msg.slice(0, 200),
          composerBeta: true,
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="jpe-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget && !generating) onCancel();
    }}>
      <div ref={panelRef} className="jpe-modal jpe-modal--xl" role="dialog"
        aria-modal="true" aria-labelledby="plan-generation-title"
        aria-busy={generating || adopting} tabIndex={-1}>
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Préparation du plan</p>
            <h3 id="plan-generation-title" className="jpe-modal__title">Nouveau plan nutritionnel</h3>
          </div>
          <button onClick={onCancel} disabled={generating} className="jrn-btn jrn-btn--ghost">Fermer</button>
        </header>

        {!result && (
          <div className="jpe-modal__body">
            {/* BC.5G.6 : la directive vit dans la sidebar atelier (source unique).
                Ici on affiche juste un récap en lecture seule pour confirmer
                ce qui sera injecté dans la génération. */}
            <div className="jpe-modal-directive-recap">
              <div className="jpe-modal-directive-recap__head">
                <span className="jpe-modal-directive-recap__label">✦ Directive utilisée</span>
                <span className="jpe-modal-directive-recap__hint">éditable dans la sidebar atelier</span>
              </div>
              {draftDirectives ? (
                <p className="jpe-modal-directive-recap__text">{draftDirectives}</p>
              ) : (
                <p className="jpe-modal-directive-recap__empty">
                  Aucune directive personnalisée — la génération utilisera les sources autorisées
                  (anamnèse, objectifs, résultats Ortho-Analytic confirmés et seules valeurs MGD validées « programme »). Pour orienter le ton/style, renseigne la
                  directive dans la sidebar de l'atelier.
                </p>
              )}
            </div>

            {/* Structure des repas : choix praticienne explicite, sans
                présélection issue du questionnaire. */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--jrn-text)', marginBottom: 6 }}>
                Structure des repas
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {MEAL_STRUCTURES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    disabled={generating}
                    onClick={() => setMealStructure(s.id)}
                    style={{
                      padding: '6px 12px', borderRadius: 999, fontSize: 12,
                      cursor: generating ? 'not-allowed' : 'pointer',
                      border: mealStructure === s.id ? '1px solid #7e5ec7' : '1px solid var(--jrn-border, rgba(0,0,0,.15))',
                      background: mealStructure === s.id ? 'rgba(167,139,250,.15)' : 'transparent',
                      color: mealStructure === s.id ? '#7e5ec7' : 'var(--jrn-text-muted)',
                      fontWeight: mealStructure === s.id ? 600 : 400,
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {!mealStructure && (
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--jrn-warn)', lineHeight: 1.5 }}>
                  Choix requis avant la génération.
                </div>
              )}
            </div>


            {/* Composition profil-aware validée et toujours active. */}
            <div className="jpe-composer-beta">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#7e5ec7', display: 'flex', alignItems: 'center', gap: 8 }}>
                    ✨ Composition profil-aware
                    <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: 'rgba(167, 139, 250, 0.15)', color: '#7e5ec7', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                      Toujours active
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--jrn-text-muted)', marginTop: 6, lineHeight: 1.55 }}>
                    Analyse l'anamnèse et applique les garde-fous ainsi que les modules validés disponibles pour cette cliente.
                  </div>
                  {detectedProfile && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#7e5ec7' }}>
                      Profil identifié : <strong>{detectedProfile.name || detectedProfile.id || 'profil clinique'}</strong>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`jrn-surface ${generationLaunchGate.serverStatusReady && !generationLaunchGate.activeMgdRequiresReview ? 'jrn-surface--quiet' : 'jrn-surface--warn'}`}
              role={generationLaunchGate.serverStatusReady && !generationLaunchGate.activeMgdRequiresReview ? 'status' : 'alert'}>
              <strong>{generationLaunchGate.usesExternalSynthesis && generationLaunchGate.serverStatusReady
                ? 'Synthèse externe prête'
                : (generationLaunchGate.usesExternalSynthesis
                  ? 'Synthèse externe à vérifier'
                  : 'Contexte du dossier vérifié')}</strong>
              {mgdStatusState === 'loading' && <p>Vérification en cours…</p>}
              {mgdStatusState === 'error' && <p>Impossible de vérifier le contexte serveur. La génération reste bloquée.</p>}
              {mgdStatusState === 'ready' && generationLaunchGate.usesExternalSynthesis && (
                <p>La synthèse relue sera utilisée avec l’anamnèse pour générer le plan.</p>
              )}
              {mgdStatusState === 'ready' && !generationLaunchGate.usesExternalSynthesis && mgdStatus?.reports?.length === 0 && <p>Contexte prêt : génération standard.</p>}
              {mgdStatusState === 'ready' && !generationLaunchGate.usesExternalSynthesis && mgdStatus?.reports?.length > 0 && mgdStatus.ready && <p>{mgdStatus.reports.length} rapport(s) revu(s). Les valeurs « programme » validées par Anissa seront incluses automatiquement.</p>}
              {mgdStatusState === 'ready' && !generationLaunchGate.usesExternalSynthesis && mgdStatus?.reports?.length > 0 && !mgdStatus.ready && <p>Revue MGD incomplète : retourne à « Revoir les résultats ». Grossesse, allaitement, post-partum et profils à risque doivent aussi être confirmés dans la fiche.</p>}
            </div>

            {!generating && (
              <div className="jrn-actions">
                <button
                  onClick={handleGenerate}
                  disabled={generationLaunchGate.disabled}
                  className="jrn-btn jrn-btn--primary"
                  title={!mealStructure ? 'Choisis d’abord la structure des repas' : undefined}
                >
                  ✨ Lancer la génération profil-aware
                </button>
              </div>
            )}

            {generating && (
              <div className="jpe-progress">
                <div className="jpe-progress__head">
                  <span className="jpe-progress__label">
                    {progress < 30 && 'Préparation du contexte…'}
                    {progress >= 30 && progress < 60 && 'Analyse de l’anamnèse…'}
                    {progress >= 60 && progress < 90 && 'Construction du plan nutritionnel…'}
                    {progress >= 90 && progress < 100 && 'Finalisation…'}
                    {progress >= 100 && 'Plan reçu ✓'}
                  </span>
                  <span className="jpe-progress__time">
                    {elapsed.toFixed(0)}s · {Math.round(progress)}%
                  </span>
                </div>
                <div className="jpe-progress__track">
                  <div
                    className="jpe-progress__fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="jpe-progress__hint">
                  La génération prend généralement 1 à 3 minutes. Ne fermez pas la fenêtre.
                </p>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="jpe-modal__body">
            {/* V97.x Phase 2 — Audit clinique post-génération.
                Affiche violations (phrases interdites) + complétude (micro
                manquants, évictions manquantes) si guardrails actifs. */}
            {guardrails.guardrailsState?.guardrails?.length > 0 && (
              <GuardrailsAuditBanner state={guardrails.guardrailsState} />
            )}
            {/* V97.x Phase 3-4 — Anti-slop via hook useSlopAudit (V97.27 refacto). */}
            {!mgdGenerationProof && <AntiSlopSection
              flags={slop.slopFlags}
              running={slop.slopRunning}
              rewrites={slop.slopRewrites}
              planText={result}
              onRun={async () => {
                try { await assertFreshBrowserAiAction(result); slop.runAudit(result); }
                catch (caught) { setError(caught?.message || 'Audit anti-slop bloqué.'); }
              }}
              onRewrite={async (flag) => {
                try { await assertFreshBrowserAiAction(result); await slop.requestRewrite(flag, result); }
                catch (caught) { setError(caught?.message || 'Réécriture anti-slop bloquée.'); }
              }}
              onAcceptRewrite={async (flag) => {
                try { await assertFreshBrowserAiAction(result); slop.acceptRewrite(flag, result, setResult); }
                catch (caught) { setError(caught?.message || 'Adoption anti-slop bloquée.'); }
              }}
              onRefuseRewrite={async (flag) => {
                try { await assertFreshBrowserAiAction(result); slop.refuseRewrite(flag); }
                catch (caught) { setError(caught?.message || 'Action anti-slop bloquée.'); }
              }}
            />}
            {mgdGenerationProof && <div className="jrn-surface jrn-surface--quiet" role="status">
              Audit et réécriture indisponibles pour cette ancienne version. La validation serveur fait foi.
            </div>}
            <div className="jpe-preview">
              <RenderedMarkdown text={result} />
            </div>
            <div className="jrn-actions">
              <button disabled={adopting} onClick={async () => {
                if (adoptingRef.current) return;
                // P1.2 — clairance clinique (porte Adopter). Override conscient sur HIGH.
                // Pour MGD, l'autorisation serveur ci-dessous réévalue le
                // texte final exact après toute correction d'Anissa.
                const verdict = mgdGenerationProof ? null : assertPlanClinicallyCleared(result, {
                  form: client?.form,
                });
                if (verdict && !verdict.cleared) {
                  if (!window.confirm(formatClearanceForConfirm(verdict))) return;
                  // V97.28 — override confirmé sur plan NON-clairé uniquement : on trace
                  // (fire-and-forget, non bloquant). L'adoption d'un plan déjà clairé ne
                  // passe pas ici → aucune trace (pas d'override). consultation_id null :
                  // on adopte un brouillon pas encore persisté (choix de non-intrusion).
                  void traceClinicalOverride(verdict, CLINICAL_OVERRIDE_DOORS.ADOPT, {
                    clientId: client?.id,
                  });
                }
                adoptingRef.current = true; setAdopting(true); setError(null);
                try {
                  await onAdopt(result.replace(/\r\n/g, '\n'), draftDirectives, mealStructure,
                    mgdGenerationProof, null);
                } catch (caught) {
                  setError(caught?.code ? mgdAdoptionErrorMessage(caught)
                    : caught?.message || 'La sauvegarde du plan a échoué.');
                } finally { adoptingRef.current = false; setAdopting(false); }
              }} className="jrn-btn jrn-btn--primary">
                {adopting ? 'Autorisation et sauvegarde…' : 'Adopter ce plan'}
              </button>
              <button disabled={adopting} onClick={() => {
                setResult(null); setMgdGenerationProof(null); setMgdReadinessToken(null);
                generationAttemptRef.current = null;
              }} className="jrn-btn jrn-btn--soft">
                Régénérer
              </button>
              <button onClick={onCancel} className="jrn-btn jrn-btn--ghost">
                Fermer sans adopter
              </button>
            </div>
            <p style={{ marginTop: 'var(--jrn-3)', fontSize: 'var(--jrn-text-xs)', color: 'var(--jrn-text-muted)' }}>
              {result.length.toLocaleString('fr-CH')} caractères · adopter remplace le plan actuel.
            </p>
          </div>
        )}

        {error && <div className="jrn-error" style={{ padding: '0 var(--jrn-6) var(--jrn-4)' }}>⚠ {error}</div>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Modal aperçu plein écran lisible (lecture du plan actuel)
// ═══════════════════════════════════════════════════════════════════

function PreviewModal({ text, onClose }) {
  const panelRef = useRef(null);
  useModalA11y({ onClose, panelRef });
  return (
    <div className="jpe-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div ref={panelRef} className="jpe-modal jpe-modal--xl" role="dialog"
        aria-modal="true" aria-labelledby="plan-preview-title" tabIndex={-1}>
        <header className="jpe-modal__header">
          <div>
            <p className="jrn-step-eyebrow">Aperçu</p>
            <h3 id="plan-preview-title" className="jpe-modal__title">Plan nutritionnel actuel</h3>
          </div>
          <button onClick={onClose} className="jrn-btn jrn-btn--ghost">Fermer</button>
        </header>
        <div className="jpe-modal__body">
          <div className="jpe-preview">
            <RenderedMarkdown text={text} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Render markdown léger (pas de lib externe)
// ═══════════════════════════════════════════════════════════════════

function RenderedMarkdown({ text }) {
  if (!text) return null;
  // Rendu minimaliste : titres / paragraphes / listes
  const lines = text.split('\n');
  const blocks = [];
  let buffer = [];
  let inList = false;

  const flushBuffer = () => {
    if (buffer.length === 0) return;
    blocks.push({ type: 'p', content: buffer.join(' ').trim() });
    buffer = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    if (line.startsWith('# ')) { flushBuffer(); inList = false; blocks.push({ type: 'h1', content: line.slice(2) }); return; }
    if (line.startsWith('## ')) { flushBuffer(); inList = false; blocks.push({ type: 'h2', content: line.slice(3) }); return; }
    if (line.startsWith('### ')) { flushBuffer(); inList = false; blocks.push({ type: 'h3', content: line.slice(4) }); return; }
    if (line.startsWith('#### ')) { flushBuffer(); inList = false; blocks.push({ type: 'h4', content: line.slice(5) }); return; }
    if (/^\s*[-*•]\s+/.test(line)) {
      flushBuffer();
      if (!inList) { blocks.push({ type: 'ul-start' }); inList = true; }
      blocks.push({ type: 'li', content: line.replace(/^\s*[-*•]\s+/, '') });
      return;
    }
    if (line === '') {
      flushBuffer();
      if (inList) { blocks.push({ type: 'ul-end' }); inList = false; }
      return;
    }
    buffer.push(line);
  });
  flushBuffer();
  if (inList) blocks.push({ type: 'ul-end' });

  // Rendu
  const out = [];
  let listItems = [];
  blocks.forEach((b, i) => {
    if (b.type === 'ul-start') { listItems = []; return; }
    if (b.type === 'li') { listItems.push(b.content); return; }
    if (b.type === 'ul-end') {
      out.push(<ul key={`ul-${i}`} className="jpe-md__ul">{listItems.map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>);
      listItems = [];
      return;
    }
    if (b.type === 'h1') out.push(<h1 key={i} className="jpe-md__h1">{b.content}</h1>);
    else if (b.type === 'h2') out.push(<h2 key={i} className="jpe-md__h2">{b.content}</h2>);
    else if (b.type === 'h3') out.push(<h3 key={i} className="jpe-md__h3">{b.content}</h3>);
    else if (b.type === 'h4') out.push(<h4 key={i} className="jpe-md__h4">{b.content}</h4>);
    else if (b.type === 'p') out.push(<p key={i} className="jpe-md__p">{renderInline(b.content)}</p>);
  });
  return <div className="jpe-md">{out}</div>;
}

// Rendu inline : **gras** et *italique* basiques
function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    const italMatch = remaining.match(/\*([^*]+)\*/);
    if (boldMatch && (!italMatch || boldMatch.index <= italMatch.index)) {
      if (boldMatch.index > 0) parts.push(remaining.slice(0, boldMatch.index));
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
    } else if (italMatch) {
      if (italMatch.index > 0) parts.push(remaining.slice(0, italMatch.index));
      parts.push(<em key={key++}>{italMatch[1]}</em>);
      remaining = remaining.slice(italMatch.index + italMatch[0].length);
    } else {
      parts.push(remaining);
      break;
    }
  }
  return parts;
}

// ═══════════════════════════════════════════════════════════════════
// User message minimaliste pour la génération
// ═══════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════
// PlanSection — carte éditable d'une section du plan
// ═══════════════════════════════════════════════════════════════════

function PlanSection({ index, title, content, onTitleChange, onContentChange, onDelete,
  onInsertAfter, onAi, busyAction, aiDisabled = false }) {
  const isBusy = busyAction !== null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const menuRef = useRef(null);
  const optionsRef = useRef(null);

  // V97.13.9 : signal UX — section "Fiche frigo" est extraite et exportée
  // en docx séparé (V92.6) via le bouton "Ouvrir l'éditeur fiche frigo".
  // Elle n'apparaîtra PAS dans le Word principal du plan. Anissa doit savoir
  // que cette section a un comportement spécial pour éviter la confusion
  // "où est passée la section 5 dans le Word ?".
  const isFridgeSection = /fiche\s*frigo|^\s*frigo\b/i.test(title || '');

  // Close menus on outside click
  useEffect(() => {
    if (!menuOpen && !optionsOpen) return;
    const handler = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (optionsOpen && optionsRef.current && !optionsRef.current.contains(e.target)) setOptionsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen, optionsOpen]);

  const canRunAi = !aiDisabled && !isBusy && content && content.trim().length >= 10;

  return (
    <div className={`jpe-section${isFridgeSection ? ' jpe-section--fridge' : ''}`}>
      {isFridgeSection && (
        <div className="jpe-section__notice" role="note">
          <span className="jpe-section__notice-icon" aria-hidden="true">ℹ️</span>
          <span>
            <strong>Section exportée séparément.</strong>{' '}
            Cette Fiche Frigo sera exclue du Word principal et générée en
            <em> .docx indépendant</em> (à imprimer + plastifier) via l'onglet
            <em> Fiche frigo</em> ci-dessus.
          </span>
        </div>
      )}
      <header className="jpe-section__head">
        <span className="jpe-section__num">{index + 1}</span>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="jpe-section__title"
          placeholder="Titre de la section"
        />
        {/* Menu options : "..." discret au lieu des 2 boutons + et 🗑 toujours visibles */}
        <div ref={optionsRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setOptionsOpen((o) => !o)}
            className="jpe-section__icon-btn"
            title="Options de la section"
            aria-label="Options"
            style={{ fontSize: 16, lineHeight: 1, paddingBottom: 4 }}
          >
            ⋯
          </button>
          {optionsOpen && (
            <div className="jpe-menu jpe-menu--right">
              <button onClick={() => { onInsertAfter(); setOptionsOpen(false); }} className="jpe-menu__item">
                ＋ Ajouter une section après
              </button>
              <button onClick={() => { onDelete(); setOptionsOpen(false); }} className="jpe-menu__item jpe-menu__item--danger">
                🗑 Supprimer cette section
              </button>
            </div>
          )}
        </div>
      </header>

      <textarea
        value={content}
        onChange={(e) => onContentChange(e.target.value)}
        rows={Math.max(6, Math.min(20, (content || '').split('\n').length + 1))}
        className="jpe-section__textarea jpe-section__textarea--readable"
        placeholder="Contenu de la section…"
      />

      <footer className="jpe-section__footer">
        <span className="jpe-section__ai-label">
          {isBusy ? `✨ ${REWRITE_LABELS[busyAction]}…`
            : aiDisabled ? 'Réécriture indisponible pour cette ancienne version' : 'Réécriture disponible'}
        </span>
        {/* Menu IA déroulant : 1 seul bouton au lieu de 4 actions inline */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            disabled={!canRunAi}
            className="jrn-btn jrn-btn--soft"
            style={{ padding: '6px 14px', fontSize: 12 }}
            title={canRunAi ? 'Choisir une action assistée'
              : aiDisabled ? 'Endpoint MGD autorisé indisponible'
                : 'Section trop courte (minimum 10 caractères)'}
          >
            ✨ Améliorer ▾
          </button>
          {menuOpen && (
            <div className="jpe-menu jpe-menu--right">
              {Object.entries(REWRITE_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => { onAi(key); setMenuOpen(false); }}
                  disabled={isBusy}
                  className="jpe-menu__item"
                  title={REWRITE_HELP[key]}
                >
                  {label}
                  <span style={{ display: 'block', fontSize: 10, color: 'var(--jrn-text-muted)', marginTop: 2, fontWeight: 400 }}>
                    {REWRITE_HELP[key]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Split / Join : convertit markdown ↔ sections
// ═══════════════════════════════════════════════════════════════════

function splitPlanIntoSections(text) {
  if (!text || !text.trim()) return [];
  const lines = text.split('\n');
  const sections = [];
  let current = null;
  let preamble = [];

  for (const line of lines) {
    const headerMatch = line.match(/^##\s+(.+)$/);
    if (headerMatch) {
      if (current) sections.push(current);
      current = { title: headerMatch[1].trim(), content: '' };
    } else if (current) {
      current.content += (current.content ? '\n' : '') + line;
    } else {
      preamble.push(line);
    }
  }
  if (current) sections.push(current);

  // Si du contenu existe avant le 1er ##, on le met en section "Préambule"
  const preambleText = preamble.join('\n').trim();
  if (preambleText) {
    sections.unshift({ title: 'PRÉAMBULE', content: preambleText });
  }

  // Trim each section content
  return sections.map((s) => ({
    title: s.title,
    content: s.content.replace(/^\n+/, '').replace(/\n+$/, ''),
  }));
}

function joinSectionsIntoPlan(sections) {
  return sections
    .map((s) => `## ${s.title}\n\n${s.content}`.trim())
    .join('\n\n');
}

// ═══════════════════════════════════════════════════════════════════
// Réécriture IA — actions disponibles + prompts dédiés
// ═══════════════════════════════════════════════════════════════════

const REWRITE_LABELS = {
  rewrite:     'Reformuler pro',
  simplify:    'Simplifier',
  adapt:       'Adapter cliente',
  actionnable: 'Rendre actionnable',
};
const REWRITE_HELP = {
  rewrite:     'Reformuler le passage avec un style fluide et professionnel',
  simplify:    'Raccourcir et alléger le passage tout en gardant les recommandations clés',
  adapt:       'Adapter le ton pour la cliente (vouvoiement bienveillant, vocabulaire grand public)',
  actionnable: 'Transformer en liste d\'actions concrètes et exécutables',
};
const REWRITE_BASE = `Tu es Anissa Deroubaix, nutritionniste fonctionnelle suisse. Tu reécris un passage d'un programme nutritionnel.

RÈGLES STRICTES :
- Conserve EXACTEMENT les dosages, posologies, marques, nombres, doses et noms de compléments
- Conserve la structure markdown (titres, listes, gras)
- Réponds UNIQUEMENT avec le passage transformé — aucune introduction, aucun commentaire, aucune guillemets
- Ne change PAS le sens médical / nutritionnel du passage`;

const REWRITE_PROMPTS = {
  rewrite: `${REWRITE_BASE}

ACTION : reformuler avec un style plus fluide et professionnel. Garder la même longueur approximative.`,
  simplify: `${REWRITE_BASE}

ACTION : simplifier et raccourcir le passage. Garder uniquement l'essentiel des recommandations. Phrases plus courtes, vocabulaire plus accessible.`,
  adapt: `${REWRITE_BASE}

ACTION : adapter le ton pour la cliente — vouvoiement chaleureux et bienveillant, vocabulaire grand public, ton encourageant. Garder le contenu mais le rendre humain et personnel.`,
  actionnable: `${REWRITE_BASE}

ACTION : transformer en liste d'actions concrètes et exécutables avec des verbes d'action clairs. Format : puces avec verbes à l'infinitif (ex: "Boire 1L d'eau au réveil"). Garder dosages et marques exacts.`,
};

export function buildMinimalUserMessage(client, form = {}, directives, now = new Date(),
  externalSynthesis = '') {
  const journey = client?.journey_state || {};
  const lines = [];
  const age = calculateAgeFromBirthDate(form.dateNaissance || form.date_naissance, now)
    ?? normalizeRecordedAge(form.age);
  const identity = {
    firstName: form.prenom,
    lastName: form.nom,
    email: form.email,
    phone: form.telephone || form.phone,
    birthDate: form.dateNaissance || form.date_naissance,
    age,
  };

  // ─── Profil ─────────────────────────────────────────────────
  lines.push(`Profil cliente : ${CLIENT_FIRST_NAME_TOKEN}, ${form.sexe || 'genre non renseigné'}`);
  if (Number.isInteger(age)) lines.push(`Âge : ${age} ans`);
  if (form.poids) lines.push(`Poids : ${form.poids}`);
  if (form.taille) lines.push(`Taille : ${form.taille}`);
  if (form.objectifs) lines.push(`\nObjectifs : ${form.objectifs}`);
  if (form.symptomes) lines.push(`Symptomes : ${form.symptomes}`);
  if (form.pathologies) lines.push(`Pathologies : ${form.pathologies}`);
  if (form.allergies) lines.push(`Allergies/intolérances : ${form.allergies}`);
  if (form.activite) lines.push(`Activité : ${form.activite}`);
  if (form.sommeil) lines.push(`Sommeil : ${form.sommeil}`);
  if (form.stress) lines.push(`Stress : ${form.stress}`);
  if (form.digestion) lines.push(`Digestion : ${form.digestion}`);

  // ─── Résultats Ortho-Analytic dont l'origine est prouvée ─────
  // Les valeurs MGD, inconnues et les synthèses globales potentiellement
  // mixtes restent hors du générateur.
  const rd = filterNonMgdResultsData(journey.results_data);
  const fromPlan = rd.from_plan.filter((r) => r.value || r.synthesis);
  const external = rd.external.filter((r) => r.name && (r.value || r.synthesis));
  const hasAllowedLabResults = fromPlan.length > 0 || external.length > 0;
  const exactExternalSynthesis = String(externalSynthesis || '').replace(/\r\n/g, '\n').trim();

  if (fromPlan.length > 0) {
    lines.push(`\n--- Résultats des analyses prescrites ---`);
    fromPlan.forEach((r) => {
      lines.push(`\n[${r.test_name || r.test_code}]`);
      if (r.value) lines.push(`Valeurs : ${r.value}`);
      if (r.synthesis) lines.push(`Notes Anissa : ${r.synthesis}`);
    });
  }

  if (external.length > 0) {
    lines.push(`\n--- Analyses externes (déjà détenues par la cliente) ---`);
    external.forEach((r) => {
      lines.push(`\n[${r.name}]`);
      if (r.value) lines.push(`Valeurs : ${r.value}`);
      if (r.synthesis) lines.push(`Notes Anissa : ${r.synthesis}`);
    });
  }

  if (exactExternalSynthesis) {
    lines.push(`\n--- Synthèse externe relue du dossier pilote ---`);
    lines.push(buildExternalSynthesisDirective(exactExternalSynthesis));
  }

  // ─── Directives éditoriales Anissa ──────────────────────────
  if (directives && directives.trim()) {
    lines.push(`\n--- Directives éditoriales Anissa ---\n${directives.trim()}`);
    lines.push('Appliquer ces directives uniquement si elles restent compatibles avec les règles de sécurité, les allergies, les traitements et les données structurées validées. En cas de conflit, la sécurité et les données structurées priment.');
  }

  lines.push(hasAllowedLabResults || exactExternalSynthesis
    ? `\nGénère le plan nutritionnel personnalisé complet (sections 1 à 7) avec menus variés, listes de courses par semaine, et alternatives naturelles. Tiens compte des résultats autorisés ci-dessus pour cibler les déficits et axes prioritaires.`
    : `\nGénère le plan nutritionnel personnalisé complet (sections 1 à 7) avec menus variés, listes de courses par semaine, et alternatives naturelles.`);
  lines.push(`Si un prénom est utile, conserve exactement le jeton ${CLIENT_FIRST_NAME_TOKEN}.`);
  return minimizeAnthropicText(lines.join('\n'), identity);
}
